import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { SBP_MIN_AMOUNT_RUBLES } from 'modules/organizer/domain/constants/wallet-topup';
import { SUBSCRIPTION_EXPIRY_NOTICE } from 'modules/organizer/domain/constants/subscription-expiry-notice';
import { formatRublesFromMinorUnits, rublesToMinorUnits } from 'modules/organizer/domain/utils/money';
import { formatSubscriptionExpiryMessage } from 'modules/organizer/domain/utils/subscription-expiry-notice';
import { BuyPlanFormValues, WalletTopupFormValues } from 'modules/organizer/domain/validators/wallet-topup.validator';
import { useSubscriptionBilling } from 'modules/organizer/ui/hooks/use-subscription-billing';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React from 'react';
import { theme } from 'styles/theme/theme';
import UDCard from 'ud-ui/components/card';
import UDLink from 'ud-ui/components/link';
import UDText from 'ud-ui/components/text';
import { SubscriptionBuyPlanForm } from './subscription-buy-plan-form';
import { SubscriptionQrStep } from './subscription-qr-step';
import { SubscriptionTopupForm } from './subscription-topup-form';
import { SubscriptionTopupSuccess } from './subscription-topup-success';

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <UDText type="footnote" color={theme.colors.Gray}>
        {label}
      </UDText>
      <UDText type="subhead">{value}</UDText>
    </div>
  );
}

function Subscription() {
  const {
    tournamentId,
    wallet,
    plans,
    activePlan,
    defaultPlan,
    pendingTopup,
    isLoading,
    isCreating,
    isBuying,
    submitError,
    buyError,
    startTopup,
    payPlan,
    resetTopup,
  } = useSubscriptionBilling();

  if (!tournamentId) {
    return (
      <UDCard className="max-w-md">
        <UDText type="headline" className="mb-2">
          Выберите организатора
        </UDText>
        <UDText type="body-text" color={theme.colors.Gray} className="mb-3">
          Чтобы пополнить баланс и оплатить тариф, сначала выберите организацию.
        </UDText>
        <UDLink to={Pages.SELECT_TOURNAMENT}>Перейти к выбору</UDLink>
      </UDCard>
    );
  }

  if (isLoading) {
    return <UDText type="body-text">Загрузка...</UDText>;
  }

  const subscription = activePlan?.subscription ?? null;
  const daysLeft = subscription ? differenceInCalendarDays(parseISO(subscription.activeTo), new Date()) : null;
  const isExpiringSoon =
    daysLeft !== null && daysLeft >= 0 && daysLeft <= SUBSCRIPTION_EXPIRY_NOTICE.warnWithinDays;
  const planName = activePlan?.name ?? null;
  const suggestedTopup = Math.max(
    defaultPlan ? defaultPlan.priceInMinorUnits / 100 : SBP_MIN_AMOUNT_RUBLES,
    SBP_MIN_AMOUNT_RUBLES,
  );
  const isQrFlow = !!pendingTopup && pendingTopup.status !== 'CONFIRMED';

  const handleTopup = async (values: WalletTopupFormValues) => {
    await startTopup({
      amountInMinorUnits: rublesToMinorUnits(values.amount),
      paymentMethod: values.paymentMethod,
    });
  };

  const handleBuyPlan = async (values: BuyPlanFormValues) => {
    await payPlan({ planId: values.planId });
  };

  return (
    <div className="flex max-w-md flex-col gap-4">
      <UDCard>
        <UDText type="headline" className="mb-2">
          Баланс
        </UDText>
        <UDText type="title-1">{formatRublesFromMinorUnits(wallet?.balanceInMinorUnits ?? 0)} ₽</UDText>
      </UDCard>

      <UDCard>
        {subscription ? (
          <>
            <div className="mb-4 flex items-center justify-between">
              <UDText className="h-6" type="headline">
                Текущая подписка
              </UDText>
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium text-white ${isExpiringSoon ? 'bg-error' : 'bg-darkYellow-500'}`}
              >
                {isExpiringSoon ? 'Скоро истекает' : 'Активна'}
              </span>
            </div>
            <InfoRow label="Действует с" value={format(parseISO(subscription.activeFrom), 'dd.MM.yyyy')} />
            <InfoRow label="Действует до" value={format(parseISO(subscription.activeTo), 'dd.MM.yyyy')} />
            <InfoRow label="Тариф" value={planName ?? subscription.purchaseType} />
            {isExpiringSoon && (
              <UDText type="footnote" className="text-error mt-1 block">
                {formatSubscriptionExpiryMessage(daysLeft ?? 0, subscription.activeTo)}
              </UDText>
            )}
          </>
        ) : (
          <>
            <UDText type="headline" className="mb-2">
              Подписка не активна
            </UDText>
            <UDText type="body-text" color={theme.colors.Gray}>
              Сначала пополните баланс по СБП или картой, затем подключите тариф с кошелька.
            </UDText>
          </>
        )}
      </UDCard>

      <UDCard>
        {pendingTopup?.status === 'CONFIRMED' ? (
          <SubscriptionTopupSuccess onDone={resetTopup} />
        ) : pendingTopup ? (
          <SubscriptionQrStep topup={pendingTopup} onRetry={resetTopup} />
        ) : (
          <SubscriptionTopupForm
            initialAmount={suggestedTopup}
            isSubmitting={isCreating}
            error={submitError}
            onSubmit={handleTopup}
          />
        )}
      </UDCard>

      {!subscription && !isQrFlow && (
        <UDCard>
          <SubscriptionBuyPlanForm
            plans={plans}
            initialPlanId={defaultPlan?.id ?? 0}
            balanceInMinorUnits={wallet?.balanceInMinorUnits ?? 0}
            isSubmitting={isBuying}
            error={buyError}
            onSubmit={handleBuyPlan}
          />
        </UDCard>
      )}
    </div>
  );
}

export default Subscription;
